// views/CRM/Analytics/funilPorVendedor.ts
//
// Etapas do funil por vendedor, para a tabela "Funil por vendedor" do Analytics
// Comercial (pedido de 22/10/2026). Parte das MESMAS etapas que somam os cards —
// já recortadas pelo período e pela contagem escolhida ("pela data da etapa" ou
// "só o que aconteceu no período") —, então a linha de total da tabela bate com
// os cards. Nenhuma regra de data mora aqui.
//
// Função pura, sem I/O nem JSX: roda sob `node --test` com type-stripping.

import type { EventFunnelRow, EventStage } from './eventFunnel.ts';
import { comPendenciaNaValidacao, naFilaDaValidacao } from '../../../src/lib/validacaoDados.ts';

/** Chave da etapa derivada "Em Validação de Dados" (não existe em FUNNEL_STAGE_DEFS). */
export const CHAVE_EM_ANALISE = 'em_analise';
/** Chave da etapa derivada "Com pendência" na validação (documento, garantia ou confirmação). */
export const CHAVE_COM_PENDENCIA = 'com_pendencia';

const somarKwh = (rows: readonly EventFunnelRow[]) => rows.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);

/**
 * "Em Validação de Dados": dos termos assinados da etapa recebida, os que HOJE estão
 * na fila da analista (inclusive os que voltaram para reanálise). É um pedaço do card
 * "Termo Assinado". Quem já foi analisado e ficou com pendência NÃO entra aqui: vai
 * para `etapaComPendencia` (regra em src/lib/validacaoDados.ts, 03/11/2026).
 *
 * Quem assinou num mês anterior e segue na fila não entra no mês filtrado
 * (entra no mês em que assinou). Em "Todo o período" aparecem todos.
 */
export function etapaEmAnalise(assinado: EventStage): EventStage {
  const rows = assinado.rows.filter((r) => naFilaDaValidacao(r.status));
  return { key: CHAVE_EM_ANALISE, label: 'Em Validação de Dados', color: '#0ea5e9', rows, leads: rows.length, kwh: somarKwh(rows) };
}

/**
 * "Com pendência": dos termos assinados da etapa recebida, os que a analista já
 * decidiu uma vez e esperam o cliente ou o vendedor. Fica à parte para acompanhar,
 * sem inflar o "Em Validação de Dados".
 */
export function etapaComPendencia(assinado: EventStage): EventStage {
  const rows = assinado.rows.filter((r) => comPendenciaNaValidacao(r.status));
  return { key: CHAVE_COM_PENDENCIA, label: 'Com Pendência', color: '#f59e0b', rows, leads: rows.length, kwh: somarKwh(rows) };
}

/** Colunas da tabela, na ordem em que aparecem. */
export const COLUNAS_VENDEDOR = [
  'prospeccao', 'simulacao', 'termo_enviado', 'termo_assinado', CHAVE_EM_ANALISE, CHAVE_COM_PENDENCIA, 'termo_aprovado', 'termo_reprovado',
] as const;
export type ColunaVendedor = typeof COLUNAS_VENDEDOR[number];

export interface CelulaVendedor {
  qtd: number;
  kwh: number;
  rows: EventFunnelRow[];
}

export interface LinhaVendedor {
  /** `null` = negociações sem responsável no cadastro. */
  id: string | null;
  nome: string;
  celulas: Record<ColunaVendedor, CelulaVendedor>;
}

const celulaVazia = (): CelulaVendedor => ({ qtd: 0, kwh: 0, rows: [] });
const linhaVazia = (id: string | null, nome: string): LinhaVendedor => ({
  id,
  nome,
  celulas: Object.fromEntries(COLUNAS_VENDEDOR.map((c) => [c, celulaVazia()])) as Record<ColunaVendedor, CelulaVendedor>,
});

/**
 * Uma linha por responsável, com quantidade e kWh de cada etapa.
 *
 * Só aparece quem tem pelo menos uma negociação em alguma etapa. Ordem: kWh
 * aprovado, depois kWh assinado, depois prospecção — quem trouxe resultado
 * primeiro, e o nome desempata.
 */
export function funilPorVendedor(
  etapas: readonly Pick<EventStage, 'key' | 'rows'>[],
  nomeDe: (id: string) => string | undefined,
): LinhaVendedor[] {
  const porId = new Map<string, LinhaVendedor>();
  for (const etapa of etapas) {
    if (!(COLUNAS_VENDEDOR as readonly string[]).includes(etapa.key)) continue;
    const coluna = etapa.key as ColunaVendedor;
    for (const r of etapa.rows) {
      const id = r.responsavel_id ? String(r.responsavel_id) : null;
      const chave = id ?? '__sem__';
      let linha = porId.get(chave);
      if (!linha) {
        linha = linhaVazia(id, id ? (nomeDe(id) || 'Responsável desconhecido') : 'Sem responsável');
        porId.set(chave, linha);
      }
      const celula = linha.celulas[coluna];
      celula.qtd += 1;
      celula.kwh += r.consumo_medio_kwh || 0;
      celula.rows.push(r);
    }
  }
  return [...porId.values()].sort((a, b) =>
    b.celulas.termo_aprovado.kwh - a.celulas.termo_aprovado.kwh
    || b.celulas.termo_assinado.kwh - a.celulas.termo_assinado.kwh
    || b.celulas.prospeccao.qtd - a.celulas.prospeccao.qtd
    || a.nome.localeCompare(b.nome, 'pt-BR'));
}

/** Soma das linhas — o que a linha de total da tabela mostra. */
export function totalDoFunil(linhas: readonly LinhaVendedor[]): LinhaVendedor {
  const total = linhaVazia(null, 'Total');
  for (const l of linhas) {
    for (const c of COLUNAS_VENDEDOR) {
      total.celulas[c].qtd += l.celulas[c].qtd;
      total.celulas[c].kwh += l.celulas[c].kwh;
      total.celulas[c].rows.push(...l.celulas[c].rows);
    }
  }
  return total;
}
